import {
  DownloadOutlined,
  MoreOutlined,
  OpenAIOutlined,
  EyeOutlined,
  DeleteOutlined,
  FilePdfOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons'
import AiDisclaimer from '@components/AiDisclaimer'
import AppModal from '@components/AppModal'
import ModalConfirm from '@components/AppModal/ModalConfirm'
import AttachmentRedactionTag from '@components/AttachmentRedactionTag'
import ButtonLink from '@components/ButtonLink'
import { AttachmentExtensionEnum } from '@components/CaseFieldUpload/Upload'
import { Flex } from '@components/Layout'
import TooltipInfo from '@components/TooltipInfo'
import {
  ATTACHMENT_REDACTION_STATUS,
  isRedactionPlaceholder,
  normalizeRedactionStatus,
  REDACTION_PLACEHOLDER_MESSAGE,
} from '@consts/attachmentRedaction'
import { ApiService } from '@services/ApiService'
import appStore from '@stores/appStore'
import { isAttachmentTypeBlocked } from '@utils/attachmentAccessControl'
import { Dropdown, Form, Spin, Tag, Tooltip } from 'antd'
import { FormLabelAlign } from 'antd/lib/form/interface'
import cx from 'classnames'
import { observer } from 'mobx-react'
import { useEffect, useMemo, useState } from 'react'
import ReactMarkdown from 'react-markdown'

import { AppButton } from '../AppButton'
import { AppFileUpload } from '../AppFileUpload'

import AiIcon from '/assets/icon/ai-icon.png'

import './style.scss'
import { ReactComponent as CheckDuplicateIcon } from '@assets/svg/check-duplicate.svg'

import ButtonText from '../ButtonText'

import CleanupSummaryModal from './components/CleanupSummaryModal'

interface AppFormFileUploadProps {
  hideButton?: boolean
  hideViewPdfButton?: boolean
  hideUploadArea?: boolean
  className?: string
  classNameUpload?: string
  label?: string | React.ReactNode
  suffixLabel?: string | React.ReactNode
  /**
   * Rendered in the icon row, ahead of the built-in AI / view / download icons. Pass a function
   * to receive whether the upload box is unusable (disabled field, or a demo account), so a
   * control placed here cannot stay live on a field that cannot accept files.
   */
  prefixActions?:
    | React.ReactNode
    | ((uploadDisabled: boolean) => React.ReactNode)
  rules?: any
  validateStatus?: any
  help?: string | React.ReactNode
  required?: boolean
  labelAlign?: string
  labelCol?: any
  wrapperCol?: any
  dragMessages?: string
  name?: string
  type?: string[]
  description?: string
  multiUpload?: boolean
  maxSize?: number
  previewTemplate?: any
  fileList?: any[]
  disabled?: boolean
  attachmentTypeId?: string
  isViewMergeFile?: boolean
  onChange?: (files: any[], file: any) => void
  beforeUpload?: () => void
  // Fires once every file picked in one go has finished uploading (or failed).
  onUploadsSettled?: () => void
  disabledDelete?: boolean
  // Locks deletion of individual files on top of disabledDelete — returns true for a file that
  // may not be removed. Composed with disabledDelete, never a replacement for it.
  isDeleteDisabledForFile?: (file: any) => boolean
  tenantId?: string
  tooltipMessage?: string
  onRemoveAll?: () => void
  extensions?: AttachmentExtensionEnum[]
  showStaticFileList?: boolean
  /**
   * Tenant Attachment Access Control: true when the field's attachmentType is in
   * the tenant offshore block list for the current user. Offshore staff still
   * MANAGE these documents — upload, delete and cleanup stay available and the
   * field looks exactly like it does for onshore staff — they just cannot read
   * the content, so only Summary / View PDF / Download (and "Download all") are
   * hidden. Set by CaseFieldUpload; individual rows can also resolve it from
   * their own file.attachmentType.
   */
  contentAccessBlocked?: boolean
}
export const AppFormFileUpload = ({
  hideUploadArea = false,
  showStaticFileList = false,
  className = '',
  classNameUpload = '',
  label,
  suffixLabel,
  prefixActions,
  rules = null,
  validateStatus = null,
  help = null,
  required = false,
  labelAlign = 'left',
  labelCol,
  wrapperCol,
  dragMessages = 'Drag and drop here or Click to browse',
  name = 'file',
  type = [],
  description = null,
  multiUpload = false,
  maxSize = 100,
  previewTemplate = null,
  fileList = [],
  disabled = false,
  attachmentTypeId = null,
  isViewMergeFile = false,
  onChange = (files, file) => {},
  beforeUpload = () => {},
  onUploadsSettled,
  disabledDelete = false,
  isDeleteDisabledForFile,
  tenantId = null,
  tooltipMessage = '',
  onRemoveAll = () => {},
  extensions = [],
  hideButton = false,
  hideViewPdfButton = false,
  contentAccessBlocked = false,
}: AppFormFileUploadProps) => {
  const [attachments, setAttachments] = useState([])
  const [isOpenAttachmentList, setIsOpenAttachmentList] = useState(false)
  const [key, setKey] = useState(Date.now())
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [currentFileIndex, setCurrentFileIndex] = useState(null)
  const [isOpenSummary, setIsOpenSummary] = useState(false)
  const [summaryTitle, setSummaryTitle] = useState('')
  const [summaryText, setSummaryText] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const isPdfFile = fileName => {
    if (!fileName) {
      return false
    }
    return fileName.toLowerCase().endsWith('.pdf')
  }

  const handleOnChange = (files, file) => {
    setAttachments(files)
    onChange(files, file)
  }

  const beforeUpload_ = () => {
    if (!!attachments.length && !multiUpload) {
      handleRemoveFile(0)
    }
    beforeUpload && beforeUpload()
  }

  const handleRemoveFile = index => {
    const newAttachments = [...attachments]
    const files = newAttachments.splice(index, 1)
    setAttachments([...newAttachments])
    onChange(newAttachments, { ...files[0], status: 'removed' })
    if (newAttachments.length === 0) {
      setIsOpenAttachmentList(false)
    }
  }

  const renderTitle = () => {
    return attachments && !!attachments.length
      ? `(${attachments.length} ${attachments.length > 1 ? 'files' : 'file'})`
      : !isViewMergeFile
      ? disabled
        ? '(No file)'
        : ``
      : '(No Merge File)'
  }

  useEffect(() => {
    setAttachments(fileList)
  }, [fileList])

  const handleDownloadFile = file => {
    ApiService.File.Download(file)
  }

  // Composes with disabledDelete rather than replacing it: a caller that sets the blanket flag
  // AND a predicate means "locked, plus these extra ones", never "the predicate wins". With no
  // predicate this is exactly disabledDelete, so existing callers are unaffected.
  const isFileDeleteDisabled = (file: any) =>
    disabledDelete || (isDeleteDisabledForFile?.(file) ?? false)

  // Redacted copy is a standalone file descriptor — reuse the normal token
  // download flow with the redacted file id and a "redacted_" browser filename.
  const handleDownloadRedactedFile = file => {
    ApiService.File.Download({
      fileId: file.redactedFileId,
      name: `redacted_${file.name || file.fileName}`,
    })
  }

  const handleOpenRedactedPDFViewer = file => {
    appStore.openPDFViewer(
      file.redactedFileId,
      `${file.name || file.fileName} (Redacted)`
    )
  }

  const handleRemoveAllFiles = () => {
    appStore.openConfirmModal({
      message: 'Do you want delete all files?',
      onOk: () => {
        onRemoveAll()
      },
    })
  }

  const handleSummaryDocument = async attachment => {
    if (!attachment.summaryText) {
      setIsLoading(true)
      try {
        const res = await ApiService.Attachment.SummaryDocument(attachment.id)
        attachment.summaryText = res.data
      } finally {
        setIsLoading(false)
      }
    }

    setIsOpenSummary(true)
    setSummaryText(attachment.summaryText)
    setSummaryTitle(attachment.name)
  }

  const [cleanupSummaryData, setCleanupSummaryData] = useState(null)
  const [isOpenCleanupSummary, setIsOpenCleanupSummary] = useState(false)
  const handleCleanupFile = async attachmentId => {
    setIsLoading(true)

    try {
      const resp = await ApiService.Attachment.CleanupFile(attachmentId)
      const cleanupSummary = resp.data

      setIsOpenCleanupSummary(true)
      setCleanupSummaryData(cleanupSummary)

      if (resp.data) {
        appStore.showSuccessMessage.customMessage(
          'File has been cleaned up successfully'
        )
      } else {
        appStore.showSuccessMessage.customMessage(
          'File already cleaned up. No action required'
        )
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleOpenPDFViewer = file => {
    appStore.openPDFViewer(file.fileId, file.name || file.fileName)
  }

  const visibleExtensions = useMemo(() => {
    if (appStore.isAdminOrStaff) {
      if (!extensions) {
        return [AttachmentExtensionEnum.CLEAN_UP_FILE]
      }
      if (!extensions.includes(AttachmentExtensionEnum.CLEAN_UP_FILE)) {
        return [...extensions, AttachmentExtensionEnum.CLEAN_UP_FILE]
      }
    }
    return extensions
  }, [extensions])

  const summaryEnabled =
    visibleExtensions?.includes(AttachmentExtensionEnum.SUMMARY) &&
    appStore.isAIFeatureEnabled('AIDocumentSummary')

  const cleanupFileEnabled = visibleExtensions?.includes(
    AttachmentExtensionEnum.CLEAN_UP_FILE
  )

  // Field-level content restriction, used for the header buttons that act on the
  // whole list. A single blocked file is enough: "Download all" mints one token for
  // the entire batch, which the backend refuses as soon as one blocked type is in it.
  const isFieldContentBlocked =
    contentAccessBlocked ||
    (attachments || []).some(file =>
      isAttachmentTypeBlocked(file.attachmentType)
    )

  const renderFileItem = (file, index, variant: 'modal' | 'static') => {
    const isStatic = variant === 'static'
    // Two independent restrictions, deliberately kept apart:
    //
    // - isContentBlocked (Attachment Access Control): the parent field is blocked
    //   (contentAccessBlocked prop from CaseFieldUpload) or this individual file's
    //   attachmentType is in the tenant block list (covers free-floating uses of
    //   AppFormFileUpload). Only the three CONTENT actions go away — the row looks
    //   and behaves like any onshore row otherwise, including Delete.
    // - isRedactionPlaceholderRow: offshore staff, Unsupported/Failed file whose
    //   fileId was blanked by the backend — metadata only, so no actions at all.
    const isRedactionPlaceholderRow = isRedactionPlaceholder(file)
    // "Redacted" indicator: the document has a published redacted copy. Onshore
    // users still see the original file; offshore staff are served the redacted
    // copy transparently — either way the green tag tells them redaction is done.
    const isRedactedRow =
      normalizeRedactionStatus(file.redactionStatus) ===
      ATTACHMENT_REDACTION_STATUS.Redacted
    // Admin / Admin Staff can open BOTH versions of a redacted document (the
    // row's fileId is the original for onshore users). Offshore staff never get
    // a choice — the backend already swaps fileId to the redacted copy and
    // blanks redactedFileId, so this stays false for them regardless of role.
    const canChooseRedactionVersion =
      isRedactedRow &&
      !!file.redactedFileId &&
      (appStore.isTenantAdmin || appStore.isAdminStaff) &&
      !appStore.ifOffshoreStaff()
    const isContentBlocked =
      contentAccessBlocked || isAttachmentTypeBlocked(file.attachmentType)
    // Only a redaction placeholder gets the greyed dashed-border treatment. A
    // content-blocked row must be visually identical to an onshore row.
    const containerClass = isRedactionPlaceholderRow
      ? isStatic
        ? 'flex items-center justify-between px-2 mb-1 rounded border border-dashed border-gray-300 bg-gray-100 text-gray-500'
        : 'flex items-center justify-between rounded p-1 border border-dashed border-gray-300 bg-gray-100 text-gray-500 px-2 file-item-container'
      : isStatic
      ? 'flex items-center justify-between px-2 mb-1 rounded bg-[#F3F5F7] hover:bg-[#e5e7eb] transition-colors group'
      : 'flex items-center justify-between rounded p-1 bg-[#F3F5F7] px-2 file-item-container'
    const labelClass = isStatic
      ? 'flex items-center gap-1 flex-1 overflow-hidden'
      : 'font-normal flex gap-1 items-center'
    const actionsClass = isStatic
      ? 'flex items-center gap-0 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 flex-shrink-0'
      : 'flex gap-0 item-actions items-center'
    // A redaction placeholder has no file behind it, so it gets no actions at all.
    // A content-blocked row keeps its action bar — Cleanup and Delete stay; the
    // three content buttons opt out individually below.
    const showActions =
      (isStatic || !appStore.isDemoAccount) && !isRedactionPlaceholderRow
    const fileName = file.name || file.fileName
    const itemKey = isStatic ? file.fileId || file.id || index : index

    return (
      <div className={containerClass} key={itemKey}>
        <div className={labelClass}>
          {file.isCleanup && (
            <span
              title="The file has been cleaned up."
              className={isStatic ? 'mr-1 flex-shrink-0' : 'mr-1'}
            >
              <CheckDuplicateIcon
                style={{
                  width: '16px',
                  height: '16px',
                  display: 'inline-block',
                }}
              />
            </span>
          )}
          {isRedactionPlaceholderRow && (
            <Tag
              color="orange"
              className="flex-shrink-0"
              title={REDACTION_PLACEHOLDER_MESSAGE}
            >
              Not redacted
            </Tag>
          )}
          {/* Every pipeline status, not just the finished one. Pending and Processing
              were previously invisible here, so a document being redacted looked
              identical to one nothing had ever happened to — and a Failed one looked
              identical to a healthy one. Onshore staff can click through to the reason;
              offshore staff get the placeholder tag above instead. */}
          {!isRedactionPlaceholderRow && (
            <span className="flex-shrink-0" onClick={e => e.stopPropagation()}>
              <AttachmentRedactionTag
                status={file.redactionStatus}
                attachmentId={file.id}
              />
            </span>
          )}
          {/* Marks a file whose Microsoft Purview protection was removed on the way in, so a
              decrypted document is distinguishable from an ordinary one at a glance. Metadata
              only — the offshore policy withholds content, not labels. */}
          {file.wasPurviewProtected && (
            <Tooltip
              title={
                file.purviewLabelName
                  ? `Sensitivity label "${file.purviewLabelName}" was removed when this file was uploaded, so the document is stored readable.`
                  : 'A Microsoft Purview sensitivity label was removed when this file was uploaded, so the document is stored readable.'
              }
            >
              <SafetyCertificateOutlined className="flex-shrink-0 text-green-500" />
            </Tooltip>
          )}
          {isStatic ? (
            <>
              <span className="text-sm text-gray-700 truncate">{fileName}</span>
              {/* > 0, not a bare truthiness check: `0 && <span/>` renders the 0 itself. */}
              {file.numberOfPages > 0 && (
                <span className="text-sm text-gray-500 flex-shrink-0">
                  {' '}
                  - {file.numberOfPages}{' '}
                  {file.numberOfPages > 1 ? 'pages' : 'page'}
                </span>
              )}
              {file.creationTime && (
                <span className="text-sm text-gray-500 flex-shrink-0">
                  {' '}
                  - {appStore.formatDateTime(file.creationTime)}
                </span>
              )}
            </>
          ) : (
            <>
              {fileName}
              {file.numberOfPages
                ? ` - ${file.numberOfPages} ${
                    file.numberOfPages > 1 ? 'pages' : 'page'
                  }`
                : ''}
              {file.creationTime
                ? ` - ${appStore.formatDateTime(file.creationTime)}`
                : ''}
            </>
          )}
        </div>

        {showActions && (
          <div className={actionsClass}>
            {summaryEnabled &&
              !isContentBlocked &&
              isPdfFile(file.fileName) && (
                <ButtonText
                  onClick={() => handleSummaryDocument(file)}
                  title="View Summary"
                >
                  <span className="flex items-center justify-center w-6 h-6">
                    <img
                      src={AiIcon}
                      alt="AI"
                      className="w-6 h-6 cursor-pointer hover:opacity-80 transition-opacity"
                    />
                  </span>
                </ButtonText>
              )}
            {cleanupFileEnabled && !file.isCleanup && file.id && (
              <ButtonText
                onClick={() => handleCleanupFile(file.id)}
                title="Clean up the file. Remove duplicates and blank pages."
              >
                <CheckDuplicateIcon style={{ width: '16px', height: '16px' }} />
              </ButtonText>
            )}
            {/* Tenant Attachment Access Control: View PDF and Download below are
                the two content actions, so they drop out when the row is blocked.
                Delete stays — offshore staff still manage the document, they just
                cannot read it. The backend enforces the same split. */}
            {isPdfFile(file.fileName) &&
              !hideViewPdfButton &&
              !isContentBlocked &&
              (canChooseRedactionVersion ? (
                <Dropdown
                  trigger={['click']}
                  menu={{
                    items: [
                      {
                        key: 'view-original',
                        label: 'View original',
                        onClick: () => handleOpenPDFViewer(file),
                      },
                      {
                        key: 'view-redacted',
                        label: 'View redacted version',
                        onClick: () => handleOpenRedactedPDFViewer(file),
                      },
                    ],
                  }}
                >
                  {/* Plain span trigger: ButtonText doesn't forward the props
                      antd Dropdown injects into its child. */}
                  <span>
                    <ButtonText title="View PDF (original / redacted)">
                      <FilePdfOutlined />
                    </ButtonText>
                  </span>
                </Dropdown>
              ) : (
                <ButtonText
                  onClick={() => handleOpenPDFViewer(file)}
                  title="View PDF"
                >
                  <FilePdfOutlined />
                </ButtonText>
              ))}
            {!isContentBlocked &&
              (canChooseRedactionVersion ? (
                <Dropdown
                  trigger={['click']}
                  menu={{
                    items: [
                      {
                        key: 'download-original',
                        label: 'Download original',
                        onClick: () => handleDownloadFile(file),
                      },
                      {
                        key: 'download-redacted',
                        label: 'Download redacted version',
                        onClick: () => handleDownloadRedactedFile(file),
                      },
                    ],
                  }}
                >
                  <span>
                    <ButtonText title="Download (original / redacted)">
                      <DownloadOutlined />
                    </ButtonText>
                  </span>
                </Dropdown>
              ) : (
                <ButtonText
                  onClick={() => handleDownloadFile(file)}
                  title="Download"
                >
                  <DownloadOutlined />
                </ButtonText>
              ))}
            {!isFileDeleteDisabled(file) && !appStore.isContractor && (
              <ButtonText
                onClick={() => {
                  setCurrentFileIndex(index)
                  setConfirmDelete(true)
                }}
                title="Delete"
              >
                <DeleteOutlined
                  style={isStatic ? undefined : { fill: 'red' }}
                />
              </ButtonText>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <Form.Item
      className={cx([className, 'form-file-upload'])}
      label={''}
      rules={rules}
      validateStatus={validateStatus}
      help={help}
      required={required}
      labelAlign={labelAlign as FormLabelAlign}
      labelCol={labelCol}
      wrapperCol={wrapperCol}
    >
      <div className={cx(['flex justify-between mb-2', classNameUpload])}>
        <span className="mt-2 w-full lg:w-auto flex items-center gap-2">
          <span>{label}</span>
          {renderTitle()}
          {tooltipMessage ? (
            <TooltipInfo text={tooltipMessage} />
          ) : (
            !multiUpload && <TooltipInfo text={'Only 1 document is allowed'} />
          )}
          {suffixLabel}
        </span>

        <Flex>
          {typeof prefixActions === 'function'
            ? prefixActions(disabled || appStore.isDemoAccount)
            : prefixActions}
          {summaryEnabled && !isFieldContentBlocked && (
            <img
              src={AiIcon}
              alt="AI"
              className="hover:opacity-60 w-8 h-8 inline-block cursor-pointer"
              title="Document summary is enabled"
            />
          )}
          {!hideButton && (
            <>
              {/* "View all" only lists file names — it opens no content — so it
                  stays available under Attachment Access Control. The rows inside
                  the modal hide their own content buttons. */}
              <AppButton
                className="rounded-md"
                disabled={!fileList.length}
                color="blue"
                onClick={() => setIsOpenAttachmentList(true)}
                title="View all"
                size="small"
              >
                <EyeOutlined />
              </AppButton>
              {!isFieldContentBlocked && (
                <AppButton
                  className="rounded-md"
                  disabled={!fileList.length || appStore.isDemoAccount}
                  color="green"
                  onClick={() => ApiService.File.DownloadAll(fileList)}
                  title="Download all"
                  size="small"
                >
                  <DownloadOutlined width="16" height="16" />
                </AppButton>
              )}
            </>
          )}
        </Flex>
        <AppModal
          open={isOpenAttachmentList}
          width={768}
          onCancel={() => setIsOpenAttachmentList(false)}
          footer={
            <AppButton
              color="red"
              outline
              onClick={() => setIsOpenAttachmentList(false)}
            >
              Close
            </AppButton>
          }
          title={
            <Flex className="justify-between">
              <span>{renderTitle()}</span>
              {/* Remove All disappears as soon as any one file is locked — it cannot honour
                  a per-file rule. */}
              {!attachments.some(isFileDeleteDisabled) &&
                attachments.length > 0 &&
                !appStore.isContractor && (
                  <ButtonLink
                    color="red"
                    className="text-base"
                    onClick={() => handleRemoveAllFiles()}
                  >
                    Remove All
                  </ButtonLink>
                )}
            </Flex>
          }
          className="view-attachment-list-modal"
        >
          <Spin spinning={isLoading}>
            <div className="py-2.5">
              {attachments.map((file, index) =>
                renderFileItem(file, index, 'modal')
              )}
            </div>
          </Spin>
        </AppModal>
      </div>
      <div
        className={cx({
          'border-[#d9d9d9] border rounded-md overflow-hidden p-2':
            showStaticFileList && attachments && attachments.length > 0,
        })}
      >
        {/* No "restricted" banner and no greyed wrapper: under Attachment Access
            Control the field must be indistinguishable from an onshore one. Only
            the content buttons disappear. */}
        {!disabled && !hideUploadArea && (
          <div>
            <div>
              <AppFileUpload
                key={key}
                name={name}
                readonly={disabled || appStore.isDemoAccount}
                type={type}
                description={description ?? `(Max ${maxSize}MB)`}
                previewTemplate={previewTemplate}
                maxSize={maxSize}
                title={dragMessages}
                multiUpload={multiUpload}
                fileList={attachments}
                itemRender={() => <></>}
                attachmentTypeId={attachmentTypeId}
                onChange={handleOnChange}
                beforeUpload={beforeUpload_}
                onUploadsSettled={onUploadsSettled}
                tenantId={tenantId}
              />
            </div>
          </div>
        )}

        {showStaticFileList && attachments && attachments.length > 0 && (
          <div className="mt-2">
            {attachments.map((file, index) =>
              renderFileItem(file, index, 'static')
            )}
          </div>
        )}
      </div>
      {confirmDelete && (
        <ModalConfirm
          modalParams={{
            message: 'Do you want to delete this file?',
            onOk: () => {
              if (currentFileIndex >= 0) {
                handleRemoveFile(currentFileIndex)
              }
            },
          }}
          onCancel={() => setConfirmDelete(false)}
        />
      )}

      <AppModal
        open={isOpenSummary}
        onCancel={() => setIsOpenSummary(false)}
        width={1024}
        title={
          <span>
            File summary: <span>{summaryTitle}</span>
          </span>
        }
      >
        <ReactMarkdown className="markdown">{summaryText}</ReactMarkdown>
        <AiDisclaimer className="mt-2" />
      </AppModal>

      {isOpenCleanupSummary && (
        <CleanupSummaryModal
          cleanupSummary={cleanupSummaryData}
          onClose={() => {
            setIsOpenCleanupSummary(false)
          }}
        />
      )}
    </Form.Item>
  )
}

export default observer(AppFormFileUpload)
